import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaShoppingCart,
  FaPlus,
  FaFilter,
  FaSearch,
  FaRedo,
} from "react-icons/fa";
import Loading from "../../../Extra/Loading";
import { useGlobalContext } from "../../../Context/GlobalContext";
import InitiateProcurement from "./InitiateProcurement";
import ProcurementTable from "./ProcurementTable";
import ProcurementHistory from "./ProcurementHistoryModal/ProcurementHistory";
const API_PREFIX = process.env.REACT_APP_URL_PREFIX || "";
const styles = `
.store-demand-page{padding:22px;background:#eef5fc;min-height:100vh;color:#1d2f49}
.sd-hero{display:flex;justify-content:space-between;align-items:center;gap:16px;background:#fff;border:1px solid #dce8f6;border-radius:12px;padding:20px;margin-bottom:14px;box-shadow:0 3px 12px #17365d0a}
.sd-hero-left{display:flex;align-items:center;gap:16px}
.sd-hero-icon{width:60px;height:60px;display:grid;place-items:center;background:#e2efff;color:#0873ef;border-radius:12px;font-size:27px}
.sd-title{font-size:29px;font-weight:750;margin:0;color:#1d2f49}
.sd-subtitle{color:#71829b;margin-top:3px}
.sd-primary{background:#0875f9;border:1px solid #0875f9;color:white;border-radius:8px;padding:12px 18px;font-weight:700;display:inline-flex;align-items:center;gap:9px}
.sd-primary:hover{background:#0664d7}
.sd-panel{background:#fff;border:1px solid #dce8f6;border-radius:11px;margin-bottom:14px;overflow:hidden;box-shadow:0 3px 12px #17365d08}
.sd-panel-heading{padding:14px 20px;background:#eaf3ff;color:#123f80;font-weight:750;display:flex;align-items:center;gap:12px}
.sd-filter-grid{display:grid;grid-template-columns:repeat(4,minmax(130px,1fr)) auto;gap:12px;align-items:end;padding:16px}
.sd-field label{display:block;font-size:13px;font-weight:650;color:#405777;margin-bottom:6px}
.sd-field input,.sd-field select{width:100%;height:42px;border:1px solid #d8e1ed;border-radius:7px;padding:8px 11px;color:#263953;background:white;min-width:0}
.sd-filter-actions{display:flex;gap:8px}
.sd-search-btn,.sd-reset-btn{height:42px;border-radius:7px;padding:0 15px;display:inline-flex;align-items:center;gap:8px;font-weight:650;white-space:nowrap;cursor:pointer}
.sd-search-btn{background:#0875f9;color:white;border:1px solid #0875f9}
.sd-search-btn:hover{background:#0664d7}
.sd-reset-btn{background:white;color:#1558a5;border:1px solid #8bbaff}
.sd-alert{padding:12px 15px;border-radius:8px;margin-bottom:12px;background:#fff0f0;color:#9b2222;border:1px solid #ffd3d3}
@media(max-width:1200px){.sd-filter-grid{grid-template-columns:repeat(3,minmax(150px,1fr))}}
@media(max-width:700px){.store-demand-page{padding:10px}.sd-hero{align-items:flex-start;flex-direction:column}.sd-title{font-size:23px}.sd-filter-grid{grid-template-columns:1fr 1fr}.sd-filter-actions{grid-column:1/-1}.sd-filter-actions button{flex:1;justify-content:center}}
@media(max-width:450px){.sd-filter-grid{grid-template-columns:1fr}}
`;
const dateOnly = (value) => (value ? String(value).slice(0, 10) : "");
export default function ProcurementMaster() {
  const { userData } = useGlobalContext();
  const [notCompletedData, setNotCompletedData] = useState([]);
  const [completedData, setCompletedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusDraft, setStatusDraft] = useState("All");
  const [status, setStatus] = useState("All");
  const [accountDraft, setAccountDraft] = useState("All");
  const [account, setAccount] = useState("All");
  const [fromDraft, setFromDraft] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDraft, setToDraft] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(7);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [selectedRow, setSelectedRow] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);
  const notCompletedUrl = `${API_PREFIX}store/procurements?status=NOT_COMPLETED`;
  const completedUrl = `${API_PREFIX}store/procurements?status=COMPLETED`;
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Force fresh list responses after create/update; otherwise a cached GET
      // can leave the table stale until the browser itself is refreshed.
      const refreshToken = Date.now();
      const requestConfig = {
        params: { _refresh: refreshToken },

        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
      };

      const [notCompletedResponse, completedResponse] = await Promise.all([
        axios.get(notCompletedUrl, requestConfig),

        axios.get(completedUrl, requestConfig),
      ]);

      setNotCompletedData(notCompletedResponse?.data?.data || []);

      setCompletedData(completedResponse?.data?.data || []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch procurement entries.",
      );
    } finally {
      setLoading(false);
    }
  }, [notCompletedUrl, completedUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const allCases = useMemo(() => {
    const combined = [...notCompletedData, ...completedData];

    const byId = new Map();

    combined.forEach((row, index) => {
      const key =
        row.case_id ??
        `${row.file_number || "row"}-${row.demand_number || index}`;

      byId.set(String(key), row);
    });

    // Show the most recently inserted/updated procurement first.

    // updated_at takes priority; created_at is used for records without it.

    const getLatestTimestamp = (row) => {
      const value = row.updated_at || row.last_action_date || row.created_at;

      const timestamp = value ? new Date(value).getTime() : 0;

      return Number.isFinite(timestamp) ? timestamp : 0;
    };

    return Array.from(byId.values()).sort((a, b) => {
      const timeDifference = getLatestTimestamp(b) - getLatestTimestamp(a);

      // Use case_id as a stable fallback when timestamps are equal/missing.

      return timeDifference || Number(b.case_id || 0) - Number(a.case_id || 0);
    });
  }, [notCompletedData, completedData]);

  const filteredCases = useMemo(() => {
    return allCases.filter((row) => {
      const matchesStatus =
        status === "All" ||
        String(row.current_status || "").toUpperCase() === status;

      const matchesAccount =
        account === "All" ||
        String(row.account_type || "").toUpperCase() === account;

      const demandDate = dateOnly(row.demand_date || row.intimation_date);

      const matchesFrom = !fromDate || (demandDate && demandDate >= fromDate);

      const matchesTo = !toDate || (demandDate && demandDate <= toDate);

      return matchesStatus && matchesAccount && matchesFrom && matchesTo;
    });
  }, [allCases, status, account, fromDate, toDate]);

  const totalPages = Math.max(1, Math.ceil(filteredCases.length / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const visibleCases = filteredCases.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );
  const applyFilters = () => {
    setStatus(statusDraft);
    setAccount(accountDraft);
    setFromDate(fromDraft);
    setToDate(toDraft);
    setPage(1);
  };

  const resetFilters = () => {
    setStatusDraft("All");
    setStatus("All");

    setAccountDraft("All");
    setAccount("All");
    setFromDraft("");
    setFromDate("");

    setToDraft("");
    setToDate("");
    setPage(1);
  };

  const openCreate = () => {
    setSelectedRow(null);
    setFormMode("create");
    setShowForm(true);
  };

  const openEdit = (row) => {
    setSelectedRow(row);
    setFormMode("edit");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setSelectedRow(null);
  };

  const handleSuccess = async () => {
    // Refresh the list after the save succeeds. A second cache-busted fetch

    // handles APIs where the list endpoint updates slightly after the save endpoint.

    await fetchData();

    await new Promise((resolve) => setTimeout(resolve, 600));

    await fetchData();

    closeForm();
  };

  const statuses = useMemo(
    () =>
      [
        ...new Set(
          allCases
            .map((r) => String(r.current_status || "").toUpperCase())
            .filter(Boolean),
        ),
      ].sort(),
    [allCases],
  );

  const accounts = useMemo(
    () =>
      [
        ...new Set(
          allCases
            .map((r) => String(r.account_type || "").toUpperCase())
            .filter(Boolean),
        ),
      ].sort(),
    [allCases],
  );

  return (
    <>
      <style>{styles}</style>

      <main className="store-demand-page">
        <section className="sd-hero">
          <div className="sd-hero-left">
            <div className="sd-hero-icon">
              <FaShoppingCart />
            </div>

            <div>
              <h1 className="sd-title">Store Demand Entry</h1>

              <div className="sd-subtitle">
                Manage and create store demand entries
              </div>
            </div>
          </div>

          <button className="sd-primary" onClick={openCreate}>
            <FaPlus /> New Demand
          </button>
        </section>

        <section className="sd-panel">
          <div className="sd-panel-heading">
            <FaFilter /> Filters
          </div>

          <div className="sd-filter-grid">
            <div className="sd-field">
              <label>Current Status</label>

              <select
                value={statusDraft}
                onChange={(e) => setStatusDraft(e.target.value)}
              >
                <option value="All">All</option>
                {statuses.map((s) => (
                  <option key={s} value={s}>
                    {s.replaceAll("_", " ")}
                  </option>
                ))}
              </select>
            </div>

            <div className="sd-field">
              <label>Account Type</label>

              <select
                value={accountDraft}
                onChange={(e) => setAccountDraft(e.target.value)}
              >
                <option value="All">All</option>
                {accounts.map((a) => (
                  <option key={a} value={a}>
                    {a.replaceAll("_", " ")}
                  </option>
                ))}
              </select>
            </div>

            <div className="sd-field">
              <label>From Date</label>
              <input
                type="date"
                value={fromDraft}
                onChange={(e) => setFromDraft(e.target.value)}
              />
            </div>

            <div className="sd-field">
              <label>To Date</label>
              <input
                type="date"
                value={toDraft}
                onChange={(e) => setToDraft(e.target.value)}
              />
            </div>

            <div className="sd-filter-actions">
              <button
                type="button"
                className="sd-search-btn"
                onClick={applyFilters}
              >
                <FaSearch /> Search
              </button>
              <button
                type="button"
                className="sd-reset-btn"
                onClick={resetFilters}
              >
                <FaRedo /> Reset
              </button>
            </div>
          </div>
        </section>

        {error && <div className="sd-alert">{error}</div>}

        {loading ? (
          <Loading />
        ) : (
          <ProcurementTable
            rows={visibleCases}
            totalRows={filteredCases.length}
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            onView={openEdit}
            onHistory={setHistoryRow}
          />
        )}
      </main>

      {showForm && (
        <InitiateProcurement
          mode={formMode}
          data={selectedRow}
          createdBy={userData?.id || userData?.username || "central_depot"}
          onClose={closeForm}
          onSuccess={handleSuccess}
        />
      )}

      {historyRow && (
        <div
          className="sd-history-backdrop"
          role="presentation"
          onMouseDown={(e) =>
            e.target === e.currentTarget && setHistoryRow(null)
          }
        >
          <div className="sd-history-shell">
            <div className="sd-history-head">
              <div>
                <strong>History &amp; Attachments</strong>
                <small>
                  {historyRow.file_number || "Procurement"} · Case #
                  {historyRow.case_id}
                </small>
              </div>

              <button
                onClick={() => setHistoryRow(null)}
                aria-label="Close history"
              >
                ×
              </button>
            </div>

            <div className="sd-history-content">
              {/*

                Existing ProcurementHistory owns history, reminders and document operations.

                It is intentionally kept unchanged and embedded here.

              */}

              <ProcurementHistory
                row={historyRow}
                onClose={() => setHistoryRow(null)}
              />
            </div>
          </div>
        </div>
      )}

      <style>{`

        .sd-history-backdrop{position:fixed;inset:0;background:rgba(12,28,49,.55);z-index:1050;display:flex;align-items:center;justify-content:center;padding:20px}

        .sd-history-shell{background:#fff;width:min(1100px,96vw);max-height:92vh;overflow:auto;border-radius:13px;box-shadow:0 20px 65px #0004}

        .sd-history-head{display:flex;justify-content:space-between;align-items:center;padding:15px 20px;background:#eaf3ff;border-bottom:1px solid #d7e5f7;position:sticky;top:0;z-index:4}

        .sd-history-head strong{display:block;font-size:18px;color:#173a69}.sd-history-head small{display:block;color:#657a96;margin-top:3px}

        .sd-history-head button{border:0;background:transparent;font-size:30px;line-height:1;color:#345}

        .sd-history-content{padding:14px}

      `}</style>
    </>
  );
}
